import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { openSupportChat } from "@/utils/openSupportChat";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Target, Eye, Award, Users, Shield, FileText } from "lucide-react";
import EmilyPhoto from "../assests/images/Emily.jpg";
import JakePhoto from "../assests/images/Jake.jpg";
import KevinPhoto from "../assests/images/Kevin.jpg";
import SophiaPhoto from "../assests/images/Sophia.jpg";

// Same reveal hook the home page uses (tiny threshold so tall sections
// still trigger on mobile).
const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect();
      }
    }, { threshold: 0.05, ...options });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []); // eslint-disable-line
  return [ref, isInView];
};

const STATS = [
  { value: "10K+", label: "documents generated", tone: "mint" },
  { value: "99.9%", label: "accuracy rate", tone: "black" },
  { value: "24hr", label: "support response", tone: "gray" },
  { value: "100%", label: "secure & private", tone: "black" },
];

const VALUES = [
  {
    icon: Target,
    title: "Mission",
    description: "To make financial document generation simple, accurate, and accessible to everyone. We believe everyone deserves professional-quality documents without the complexity.",
    tone: "gray",
  },
  {
    icon: Eye,
    title: "Vision",
    description: "To be the most trusted document generation platform in the USA, known for accuracy, affordability, and exceptional user experience.",
    tone: "mint",
  },
  {
    icon: Award,
    title: "Achievements",
    description: "Over 50,000 documents generated with a 99.9% accuracy rate. We've helped thousands of individuals and businesses create professional financial documents.",
    tone: "black",
  },
];

// The same testimonials the homepage shows — same people, same quotes, so
// the site tells one consistent story.
const TESTIMONIALS = [
  {
    img: JakePhoto,
    name: "Jake",
    role: "Dallas, TX",
    quote: "I needed proof of income for an apartment and didn't have a ton of options. MintSlip worked: clean, accurate stubs in under five minutes.",
  },
  {
    img: SophiaPhoto,
    name: "Sophia",
    role: "Phoenix, AZ",
    quote: "Every time I needed a document it was just there: paystubs, W-2s, even my offer letter. Everything matched perfectly and looked completely professional.",
  },
  {
    img: EmilyPhoto,
    name: "Emily",
    role: "Atlanta, GA",
    quote: "i couldn't find my old stubs to save my life n MintSlip let me remake them in like 2 minutes. def worked for me!!!! thank yoouuuu",
    stars: true,
  },
  {
    img: KevinPhoto,
    name: "Kevin",
    role: "Chicago, IL",
    quote: "My lender wanted two months of pay stubs.... I had them done the same afternoon. Don't even think about it, just use it.",
  },
];

const toneCard = {
  mint: { bg: "bg-emerald-400", big: "text-slate-900", small: "text-emerald-950" },
  black: { bg: "bg-[#0b0b0b]", big: "text-white", small: "text-slate-300" },
  gray: { bg: "bg-slate-100", big: "text-slate-900", small: "text-slate-600" },
};

export default function About() {
  const navigate = useNavigate();
  const [heroRef, heroInView] = useInView();
  const [storyRef, storyInView] = useInView();
  const [valuesRef, valuesInView] = useInView();
  const [testiRef, testiInView] = useInView();
  const [ctaRef, ctaInView] = useInView();

  return (
    <div className="min-h-screen bg-white relative" style={{ overflowX: "clip" }}>
      <Helmet>
        <title>About MintSlip | Professional Document Generation Platform</title>
        <meta name="description" content="Learn about MintSlip - your trusted partner for professional document generation. We provide accurate, affordable, and easy-to-use tools for paystubs, tax forms, and more." />
        <meta name="keywords" content="about MintSlip, document generation company, paystub generator, tax form generator" />
        <meta property="og:title" content="About MintSlip - Document Generation Platform" />
        <meta property="og:description" content="Trusted by 1,000+ users for professional document generation." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="About MintSlip" />
        <meta name="twitter:description" content="Professional document generation made simple." />
      </Helmet>

      <Header />

      {/* Hero — centered like the home hero, with the stats bento below */}
      <section ref={heroRef} className="relative max-w-[1288px] mx-auto px-6 pt-14 pb-16 md:pt-20 md:pb-20">
        <div className={`transition-all duration-700 ${heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <h1 className="font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            Your trusted partner in <span className="font-black">document generation</span>
          </h1>
          <p className="text-center text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            At MintSlip, we understand the importance of accurate financial documentation. Our commitment is to simplify the process: accurate, easy to use, and affordable for businesses, entrepreneurs, and individuals across the USA.
          </p>
          <div className="flex justify-center mb-14">
            <button className="navbar-cta" onClick={() => navigate("/app")}>
              <FileText size={18} />
              <span>Get started</span>
            </button>
          </div>

          {/* Stats bento */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {STATS.map((s) => {
              const t = toneCard[s.tone];
              return (
                <div key={s.label} className={`rounded-[28px] ${t.bg} px-4 py-10 md:px-8 md:py-14 flex flex-col items-center justify-center text-center`}>
                  <span className={`font-display font-black text-4xl sm:text-5xl md:text-6xl ${t.big}`}>{s.value}</span>
                  <span className={`mt-3 text-sm md:text-lg ${t.small}`}>{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Story */}
      <section ref={storyRef} className="max-w-[1288px] mx-auto px-6 py-16 md:py-24">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center transition-all duration-700 ${storyInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 mb-6">
              From a simple idea to <span className="font-black">industry leaders</span>
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-6">
              MintSlip began with a mission to revolutionize the way financial documents are created. Frustrated by the inaccuracy, high costs, and complexity of traditional methods, we saw an opportunity for innovation.
            </p>
            <p className="text-slate-600 text-lg leading-relaxed mb-6">
              Today, we're proud to be recognized as a trusted solution for pay stubs, accounting mockups, W-2 forms, and more. Our platform empowers individuals and businesses to generate professional documents in minutes, not hours.
            </p>
            <p className="text-slate-600 text-lg leading-relaxed">
              We believe that everyone deserves access to professional-quality financial documents without the need for expensive software or accounting expertise.
            </p>
          </div>
          <div className="rounded-[28px] bg-slate-100 p-6 md:p-10">
            <div className="grid grid-cols-2 gap-4 md:gap-6">
              {[
                { icon: FileText, title: "Pay Stubs", sub: "Professional & accurate" },
                { icon: Shield, title: "Accounting Mockups", sub: "Detailed & complete" },
                { icon: Award, title: "W-2 Forms", sub: "Compliant" },
                { icon: Users, title: "Support", sub: "24hr response" },
              ].map((c) => (
                <div key={c.title} className="bg-white rounded-2xl p-5 md:p-6 shadow-sm">
                  <span className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center mb-3">
                    <c.icon className="w-6 h-6 text-white" />
                  </span>
                  <p className="font-display font-bold text-slate-900 text-lg leading-tight">{c.title}</p>
                  <p className="text-sm text-slate-500 mt-1">{c.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section ref={valuesRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6 transition-all duration-700 ${valuesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          What <span className="font-black">drives us</span>
        </h2>
        <p className={`text-center text-lg md:text-xl text-slate-600 max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 ${valuesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Our core values guide everything we do at MintSlip.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {VALUES.map((v, i) => {
            const t = toneCard[v.tone];
            return (
              <div
                key={v.title}
                className={`rounded-[28px] ${t.bg} p-8 md:p-10 transition-all duration-700 ${valuesInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
                style={{ transitionDelay: `${150 + i * 120}ms` }}
              >
                <span className="w-14 h-14 rounded-full bg-white shadow-sm flex items-center justify-center mb-5">
                  <v.icon className="w-7 h-7 text-slate-900" />
                </span>
                <h3 className={`font-display text-2xl md:text-3xl font-medium mb-3 ${t.big}`}>{v.title}</h3>
                <p className={`leading-relaxed ${t.small}`}>{v.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Testimonials */}
      <section ref={testiRef} className="max-w-[1288px] mx-auto px-6 pb-16 md:pb-24">
        <h2 className={`font-display text-center text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-6 transition-all duration-700 ${testiInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          What our <span className="font-black">clients say</span>
        </h2>
        <p className={`text-center text-lg md:text-xl text-slate-600 max-w-2xl mx-auto mb-12 transition-all duration-700 delay-100 ${testiInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          Thousands of satisfied customers trust MintSlip for their document needs.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <div
              key={t.name}
              className={`bg-white border border-slate-200 rounded-2xl shadow-sm p-4 md:p-5 flex flex-col sm:flex-row items-center gap-5 md:gap-8 transition-all duration-700 ${testiInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
              style={{ transitionDelay: `${150 + i * 120}ms` }}
            >
              <img
                src={t.img}
                alt={`${t.name}, MintSlip customer`}
                loading="lazy"
                className="w-full sm:w-44 md:w-52 aspect-square rounded-xl object-cover flex-shrink-0"
              />
              <div className="flex-1 text-center px-1 sm:pr-4">
                <p className="text-slate-600 leading-relaxed">{t.quote}</p>
                {t.stars && (
                  <div className="mt-2 text-xl tracking-wide" role="img" aria-label="Rated 5 out of 5 stars">
                    ⭐⭐⭐⭐⭐
                  </div>
                )}
                <p className="mt-4 font-bold" style={{ color: "#1a4731" }}>{t.name}</p>
                <p className="text-slate-600">{t.role}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA — same treatment as the home page */}
      <section ref={ctaRef} className="pb-20 md:pb-28 px-6 bg-white">
        <div className={`max-w-[1288px] mx-auto text-center transition-all duration-700 ${ctaInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-slate-900 mb-5" style={{ lineHeight: 1.08 }}>
            Ready to get started?
          </h2>
          <p className="text-lg md:text-xl leading-relaxed text-slate-600 max-w-2xl mx-auto mb-8">
            Create professional pay stubs, accounting mockups, W-2 forms, and more in minutes. No complicated software required.
          </p>
          <div className="flex justify-center mb-4">
            <button className="navbar-cta" onClick={() => navigate("/app")}>
              <FileText size={18} />
              <span>Create a paystub</span>
            </button>
          </div>
          <button
            type="button"
            onClick={() => openSupportChat()}
            className="text-slate-600 underline underline-offset-4 hover:text-emerald-700 transition-colors"
          >
            Questions? Contact us.
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
